import { useState, useRef, KeyboardEvent, useEffect } from "react";
import { HorizontalMenuItemProps } from "./HorizontalMenuItem";

export type MenuItemRef = { focus: () => void };

interface ItemInfo {
    isLabel?: boolean;
}

/**
 * custom hook to utilize arrow navigation inside the horizontal menubar.
 * this fits both HorizontalMenu and AutoHorizontalMenu.
 */
export const useArrowNavigation = (items: ItemInfo[]) => {
    // Get the index of the first item that is not a label
    const firstFocusableItemIndex = items.findIndex((item) => !item.isLabel);

    const [itemIndex, setItemIndex] = useState(firstFocusableItemIndex);

    // Reset the item index when the items array changes
    // (it could happen in AutoHorizontalMenu because of changing the window width)
    useEffect(() => {
        setItemIndex(firstFocusableItemIndex);
    }, [firstFocusableItemIndex, items.length]);

    // refs array for menu-items
    const elementsRef = useRef<Array<MenuItemRef | undefined>>([]);

    // add menu-item refs to the ref array
    // used as a ref method <MemuItem ref={el => addElementRef(el, index)}/>
    const addElementRef = (element: MenuItemRef | null, index: number) => {
        if (!element) {
            return;
        }

        // new render cicle (there are meny in case of AutoHorizontalMenu) - reset the array.
        if (index === 0) {
            elementsRef.current = [];
        }

        // using useImperativeHandle inside HorizontalMenuItem causes the method to be called twice
        // we want the second one, which contains the handle.
        if (elementsRef.current[index]) {
            elementsRef.current[index] = element;
        }
        else {
            elementsRef.current.push(element);
        }
    };

    // move focus index inside menubar menu-items
    const handleKeyDown = (index: number, event: KeyboardEvent<HTMLElement>) => {
        // Skip the arrow navigation if there's nowhere to navigate to
        const focusableItemsCount = items.filter((item) => !item.isLabel).length;
        if (focusableItemsCount < 1) {
            return;
        }

        const moveToNextFocusableItem = (increment: number) => {
            // Move one item in the given direction (in a loop) until hitting an item that can be focused
            do {
                index += increment;
                if (index < 0) {
                    index = items.length - 1;
                }
                if (index >= items.length) {
                    index = 0;
                }
            } while (items[index].isLabel);

            setItemIndex(index);
            elementsRef.current[index]?.focus();
        };

        if (event.key === "ArrowLeft") {
            moveToNextFocusableItem(-1);
        }

        if (event.key === "ArrowRight") {
            moveToNextFocusableItem(1);
        }
    };

    const handleItemClick = (index: number) => {
        setItemIndex(index);
    };

    const getItemTabIndex = (index: number) => (itemIndex === index ? 0 : -1);

    // The following props should be used for each child item.
    // The technique used here is "roving tabindex" - see
    // https://www.w3.org/WAI/ARIA/apg/practices/keyboard-interface/#keyboardnavigationinsidecomponents
    const itemProps = (
        index: number
    ): Pick<HorizontalMenuItemProps, "buttonRefProp" | "tabIndex" | "onKeyDown" | "onClick"> => ({
        buttonRefProp: (element) => addElementRef(element, index),
        tabIndex: getItemTabIndex(index),
        onKeyDown: (event) => handleKeyDown(index, event),
        onClick: () => handleItemClick(index),
    });

    return { itemProps };
};
